import { Link } from 'react-router-dom';
import { Section, Sub, Card, Note, Warn, Fields, Shot, Code, New } from '../../../components/docs/Blocks';

export default function Introduction() {
  return (
    <Section
      id="introduction"
      title="Introduction"
      lead="Bedrock Toolchest is a desktop editor for Minecraft Bedrock Edition worlds. It opens the world files Minecraft writes, shows you what is inside them, and lets you change it — level settings, players, entities, villages, maps, structures and add-ons — without touching a command line."
    >
      <Sub id="whats-new" title="What is new in 3.0">
        <Card>
          <p>
            Version 3.0 is a ground-up rewrite. The program is now a single native
            application rather than a Python bundle, which is why it starts in
            about a second, opens multi-gigabyte worlds without stalling, and ships
            as one executable with nothing beside it. Since 3.0.3 it runs on Linux
            as well as Windows.
          </p>
          <Fields
            items={[
              ['A rebuilt shell', 'A rail down the left for the five areas, a list of screens beside it, and one action bar along the bottom that tells you exactly how many changes are waiting to be saved.'],
              ['The world picker is the front door', 'Every world on the machine, grouped by which Minecraft installed it, with search, folder sizes, recent worlds and a health check on each one.'],
              ['A real world map', 'The whole world rendered from its blocks, in all three dimensions, with layer options and a pruning workspace built into it rather than bolted beside it.'],
              ['Villages', 'A screen for something no other Bedrock editor shows: which villages exist, who lives in them, what they have claimed, and why one has stopped breeding.'],
              ['Block Entities', 'Every chest, sign, hopper and spawner in the world, listed and editable.'],
              ['Block Search', 'Name a block and get every coordinate it stands at, anywhere in the world, with a jump straight to it on the map.'],
              ['Tamed Pets', 'Every wolf, cat, horse, parrot and llama tamed to a player — released or handed to somebody else.'],
              ['Player transfer', 'Export a player to a file, import one back, or copy chosen parts of one player onto another.'],
              ['Structures with a 3D preview', 'Saved structures rendered in the app, with the block list that would build them.'],
              ['Backups that outlive the world', 'Kept outside the world folder, so deleting the world does not delete its backups.'],
              ['A change review', 'Nothing is written until you save, and you can see the exact list of what a save would do before it does it.'],
            ]}
          />
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Since 3.0
        </h4>
        <Card>
          <p>
            The point releases since have each added a few things. The{' '}
            <Link to="/changelog" className="text-primary-500 hover:text-primary-600 underline">changelog</Link>{' '}
            has the full list; the ones worth knowing about are described in the
            section they belong to, and marked with the release that brought them,
            like <New>New in 3.1</New>.
          </p>
          <Fields
            items={[
              ['Realms (3.1)', 'Sign in with Microsoft to see the Realms you own, download a world, edit it like any other and upload it back. Rename, open or close a Realm, switch its active world, manage its players and invite link, and restore its backups. See Realms & Servers.'],
              ['Dedicated servers (3.1)', "Connect to your own Bedrock servers over SFTP, change a world's settings and add-ons in place or download the whole world, and edit the server's own settings."],
              ['Every remote change can be undone (3.1)', 'Whatever Toolchest changes on a server or Realm is backed up first and listed on the Backups page, ready to put back.'],
              ['Copy a pet or mob to another world (3.1)', 'Copy it from Entities or Tamed Pets, then paste it at a player in any world.'],
              ['Icons and experiments from Minecraft (3.1)', "Block and item icons drawn from the game's own textures, and experiments with the game's own names, up to 1.26.52 and Preview 1.26.70."],
              ['Item clipboard (3.0.4)', 'Copy an item out of one inventory and paste it into another slot, another player or another world. The copy outlives the world it came from.'],
              ['Your own translations (3.0.4)', 'The language files sit in a folder you can open from Settings. Edit one and your changes are kept through updates.'],
              ['Earlier console sessions (3.0.4)', 'The Console can show the log of any of the last ten sessions, including one that crashed.'],
              ['Mouse Back and Forward (3.0.4)', 'The side buttons on a mouse move through the screens you have visited, as they do in a browser.'],
              ['Linux (3.0.3)', 'A self-contained 64-bit build with the .NET runtime inside. Validated on Ubuntu 24.04.'],
              ['Dimension transfer (3.0.3)', 'Export a custom dimension from one world and import it into another, with its add-ons identified and activated where they are installed. From the World Map.'],
              ['Area biomes (3.0.3)', 'Draw an area on the World Map and set every chunk in it to one biome. Single chunks could already be set from the Chunk Inspector since 3.0.1.'],
              ['Players on the map (3.0.2, 3.0.3)', 'Head markers for every player and a marker for the world spawn. Click a head to open that player; find a player from their Identity card.'],
              ['Custom items (3.0.1)', 'Build any item from scratch — name, lore, enchantments, trim, durability and the rules that keep it on death or lock it in place — and drop it into an inventory.'],
              ['Inside containers (3.0.3)', 'Shulker boxes and bundles open in the Inventory tab as slots of their own, at any depth.'],
            ]}
          />
        </Card>

        <Note>
          Version 2.8.7 documentation is still available if you have not upgraded —
          use the version switcher above, or go straight to the{' '}
          <Link to="/docs/v2" className="underline">2.8 documentation</Link>.
        </Note>
      </Sub>

      <Sub id="getting-started" title="Getting started">
        <Card>
          <ol className="space-y-2 list-decimal list-inside">
            <li>
              Download the latest version from the{' '}
              <Link to="/download" className="text-primary-500 hover:text-primary-600 underline">
                download page
              </Link>.
            </li>
            <li>
              On Windows, run the .exe. On Linux, extract the archive and run{' '}
              <Code>./BedrockToolchest</Code> from the folder it makes. There is no
              installer and nothing to set up either way.
            </li>
            <li>
              Pick a world from the list, or use <strong>Browse for level.dat</strong> if
              your worlds live somewhere Toolchest does not look.
            </li>
            <li>Click <strong>Open World</strong>.</li>
            <li>Make your changes. Nothing is written to the world while you work.</li>
            <li>
              Click <strong>Save Changes</strong>. A backup is taken first, and every
              change is read back afterwards to confirm it landed.
            </li>
          </ol>
        </Card>

        <Shot
          name="picker"
          alt="The Bedrock Toolchest world picker, listing every Minecraft world on the computer"
          caption="The world picker: recently opened worlds along the top, every world on the machine below, and the details of the selected one on the right."
        />
      </Sub>

      <Sub id="safety" title="How your world is kept safe">
        <Card>
          <p>Three things stand between you and a broken world, and all three are on by default.</p>
          <Fields
            items={[
              ['Nothing is written while you edit', 'Every change is staged in memory. The world on disk is untouched until you press Save, and Revert throws the lot away.'],
              ['A backup before the first save of each session', 'A full copy of the world, kept outside the world folder so it survives the world being deleted. Taken once per session rather than once per save, because a backup taken before the third save already contains the first two.'],
              ['The save is checked afterwards', 'Every change is read back out of the world once written. If a save fails partway through, the world is rolled back to how it was rather than left half-written.'],
            ]}
          />
        </Card>

        <Warn>
          Always close the world in Minecraft before opening it in Toolchest, and
          close it in Toolchest before opening it in Minecraft. Toolchest holds the
          world’s database lock while it is open and will tell you if Minecraft — or
          anything else — is already holding it, but the safe habit is one program at
          a time.
        </Warn>
      </Sub>

      <Sub id="requirements" title="System requirements">
        <Card>
          <Fields
            items={[
              ['Operating system', 'Windows 10 or later, 64-bit; or a 64-bit x86 Linux desktop. Ubuntu 24.04 is what the Linux build is tested on — other distributions should work but have not been tried, and ARM machines are not supported.'],
              ['On Linux', 'Nothing to install: the archive carries its own .NET runtime. It needs a graphical desktop and the usual system libraries — X11, fontconfig, GTK 3, WebKitGTK for the 3D previews, Mesa. The README in the archive lists the exact Ubuntu packages if it will not start.'],
              ['Disk', 'A single executable, plus room for backups. The backup budget is 10 GB per world by default and you can change it in Settings.'],
              ['Minecraft', 'Bedrock Edition worlds from 1.21 through the current release. On Windows, worlds from the Microsoft Store, the standalone launcher, Preview and Education Edition are all found automatically; on Linux, worlds from mcpelauncher are. Anything else can be opened with Browse for level.dat.'],
              ['Internet', 'Not required for worlds on this computer. The only network requests the program makes for those are the update check and, if you leave it on, player head and 3D model previews. Realms & Servers needs a connection, and on Linux a keyring (libsecret) to keep the sign-in and server passwords encrypted.'],
            ]}
          />
        </Card>
      </Sub>
    </Section>
  );
}
